import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { serviceWaLink } from "@/lib/whatsapp";
import { Reveal } from "@/components/Reveal";

export default function Services({ content }) {
  const { t, lang } = useLang();
  const s = t.services;
  const [filter, setFilter] = useState("all");

  const categories = content?.categories || [];
  const services = useMemo(
    () => (content?.services || []).filter((sv) => sv.active !== false),
    [content]
  );
  const phone = content?.contact?.phone_intl || "972555004933";

  const visibleCats = filter === "all" ? categories : categories.filter((c) => c.id === filter);

  return (
    <section id="services" className="bg-ivory py-20 md:py-32 scroll-mt-20" data-testid="services-section">
      <div className="max-w-6xl mx-auto px-5 md:px-10">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs tracking-[0.22em] uppercase text-gold font-medium">{s.overline}</span>
            <h2 className="mt-4 font-serif text-3xl md:text-5xl text-ink">{s.title}</h2>
            <p className="mt-4 text-taupe">{s.subtitle}</p>
          </div>
        </Reveal>

        {/* Filters */}
        <Reveal delay={0.05}>
          <div className="mt-10 md:mt-12 flex flex-wrap justify-center gap-2.5" data-testid="services-filters">
            <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">
              {s.all}
            </FilterChip>
            {categories.map((c) => (
              <FilterChip key={c.id} active={filter === c.id} onClick={() => setFilter(c.id)} testid={`filter-${c.id}`}>
                {c[lang] || c.fr}
              </FilterChip>
            ))}
          </div>
        </Reveal>

        {/* Category lists */}
        <div className="mt-14 space-y-16">
          <AnimatePresence mode="wait">
            <motion.div
              key={filter}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4 }}
              className="space-y-16"
            >
              {visibleCats.map((cat) => {
                const items = services.filter((sv) => sv.category === cat.id);
                if (items.length === 0) return null;
                return (
                  <div key={cat.id} data-testid={`category-${cat.id}`}>
                    <h3 className="font-serif text-2xl md:text-3xl text-ink mb-6 flex items-center gap-4">
                      <span className="h-px w-8 bg-gold/60" />
                      {cat[lang] || cat.fr}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-x-12 gap-y-1">
                      {items.map((item) => (
                        <ServiceRow key={item.id} item={item} lang={lang} s={s} phone={phone} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`rounded-full px-5 py-2 text-sm transition-colors border ${
        active ? "bg-gold text-white border-gold" : "bg-white text-taupe border-sand hover:border-gold hover:text-gold"
      }`}
    >
      {children}
    </button>
  );
}

function ServiceRow({ item, lang, s, phone }) {
  const name = item[lang]?.name || item.fr?.name;
  const link = serviceWaLink(phone, lang, name, item.price, item.quote);

  return (
    <div className="group flex items-baseline gap-3 py-4 border-b border-sand/60" data-testid={`service-${item.id}`}>
      <span className="text-ink text-base md:text-lg leading-snug">{name}</span>
      <span className="flex-1 border-b border-dotted border-sand mx-1 translate-y-[-4px]" aria-hidden="true" />
      {item.quote ? (
        <span className="text-sm text-taupe italic whitespace-nowrap">{s.quote}</span>
      ) : (
        <span className="font-serif text-lg md:text-xl text-gold whitespace-nowrap">{item.price} ₪</span>
      )}
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="ms-2 shrink-0 rounded-full border border-sand px-3.5 py-1.5 text-xs text-taupe hover:bg-gold hover:text-white hover:border-gold transition-colors whitespace-nowrap"
        data-testid={`book-${item.id}`}
      >
        {item.quote ? s.quoteBtn : s.book}
      </a>
    </div>
  );
}
